import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  BrandingProvider,
  LeaderboardList,
  LeaderboardRow,
  LeaderboardShell,
  formatLapTime,
  useOrientation,
  leaderboardCarLabels,
} from "@vms/leaderboard-ui";
import type { VenueLeaderboardQuery } from "@vms/types";
import { useVenueLeaderboard } from "../../api/leaderboards";

/**
 * Chrome-less venue-wide leaderboard for cloud-ui — mirror of hub-ui's
 * `apps/hub-ui/src/pages/display/VenueDisplay.tsx`. Cloud is multi-tenant so
 * the URL carries the `:venueId` (hub-ui has implicit single-venue scope).
 *
 * Optional URL filters via `?track=...&car=...&game=...` — same pattern as
 * hub-ui — let an operator share a focused link (e.g. all GT3 laps at Spa).
 *
 * No event-specific branding here, since the page isn't event-scoped — it
 * renders with the leaderboard-ui defaults. (Operators can build a Saved
 * Leaderboard with custom branding via `/display/leaderboard/:id` when they
 * want a branded venue-wide view.)
 */
export function VenueDisplay() {
  const { venueId } = useParams<{ venueId: string }>();
  const [searchParams] = useSearchParams();
  const { isLandscape } = useOrientation();

  const filters: VenueLeaderboardQuery = useMemo(() => ({
    tracks: searchParams.get("track") ? [searchParams.get("track")!] : undefined,
    cars: searchParams.get("car") ? [searchParams.get("car")!] : undefined,
    game: (searchParams.get("game") as "assetto-corsa" | "rfactor-2" | null) ?? undefined,
  }), [searchParams]);

  const { data: entries } = useVenueLeaderboard(venueId, filters);

  const leaderboard = entries ?? [];
  const carLabels = leaderboardCarLabels(leaderboard);
  const subtitle =
    [filters.tracks?.[0], filters.cars?.[0], filters.game].filter(Boolean).join(" · ") ||
    "All Events";

  // Compute fastest sectors across the visible entries for highlighting — same
  // logic as hub-ui.
  const fastestSectors = useMemo(() => {
    let s1: number | null = null;
    let s2: number | null = null;
    let s3: number | null = null;
    for (const e of leaderboard) {
      if (e.bestLapSector1Ms != null && (s1 == null || e.bestLapSector1Ms < s1)) s1 = e.bestLapSector1Ms;
      if (e.bestLapSector2Ms != null && (s2 == null || e.bestLapSector2Ms < s2)) s2 = e.bestLapSector2Ms;
      if (e.bestLapSector3Ms != null && (s3 == null || e.bestLapSector3Ms < s3)) s3 = e.bestLapSector3Ms;
    }
    return { sector1Ms: s1, sector2Ms: s2, sector3Ms: s3 };
  }, [leaderboard]);

  return (
    <BrandingProvider branding={null}>
      <LeaderboardShell title="Venue Leaderboard" subtitle={subtitle} mode="venue">
        {leaderboard.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-lg">
            No lap times recorded yet.
          </div>
        ) : (
          <LeaderboardList
            totalCount={leaderboard.length}
            renderEntry={(index) => {
              const entry = leaderboard[index];
              return (
                <LeaderboardRow
                  position={entry.position}
                  driverName={`${entry.driver.firstName} ${entry.driver.lastName}`}
                  companyName={entry.driver.companyName || undefined}
                  car={carLabels[index]}
                  lapTime={formatLapTime(entry.bestLapTimeMs)}
                  sectors={{
                    s1: entry.bestLapSector1Ms,
                    s2: entry.bestLapSector2Ms,
                    s3: entry.bestLapSector3Ms,
                  }}
                  fastestSectors={fastestSectors}
                  showSectors={isLandscape}
                  extraColumns={[{ label: "Track", value: entry.trackName ?? entry.track }]}
                />
              );
            }}
          />
        )}
      </LeaderboardShell>
    </BrandingProvider>
  );
}
